import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../../../AxiosInstance";
import { toast, ToastTypes } from "../../../components/ajonjolib/toasts/toast/toast";
import Toggle from "./toggle";
import styles from "../company.module.css";

// Mismos grupos que el filtro de la zona de alertas del detalle.
const CATEGORIES = [
  { field: "alert_email_device", key: "device" },
  { field: "alert_email_power", key: "power" },
  { field: "alert_email_system", key: "system" },
];

const EMPTY_NEW = { email: "", name: "", alert_email_device: true, alert_email_power: true, alert_email_system: false };

function firstError(body, fallback) {
  if (!body || typeof body !== "object") return fallback;
  for (const value of Object.values(body)) {
    if (Array.isArray(value) && value.length) return value[0];
    if (typeof value === "string") return value;
  }
  return fallback;
}

function CategoryToggles({ row, onToggle, busy, t }) {
  return CATEGORIES.map((c) => (
    <div key={c.field} className={styles.centerCell}>
      <span className={styles.mobileLabel}>{t(`company.emails.cat.${c.key}`)}</span>
      <Toggle
        checked={row[c.field]}
        disabled={busy}
        label={t(`company.emails.cat.${c.key}`)}
        onChange={(value) => onToggle(c.field, value)}
      />
    </div>
  ));
}

export default function AlertEmailsCard({ data, setData }) {
  const { t } = useTranslation();
  const { company, users, recipients } = data;
  const [busy, setBusy] = useState({});
  const [newRecipient, setNewRecipient] = useState(EMPTY_NEW);
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const setRowBusy = (key, value) => setBusy((b) => ({ ...b, [key]: value }));

  // Cambio optimista: se pinta ya y se revierte si el backend falla.
  const patchList = (listKey, id, changes) =>
    setData((d) => ({
      ...d,
      [listKey]: d[listKey].map((row) => (row.id === id ? { ...row, ...changes } : row)),
    }));

  const toggleMaster = (value) => {
    setData((d) => ({ ...d, company: { ...d.company, alert_emails_enabled: value } }));
    setRowBusy("master", true);
    axiosInstance
      .patch("company/settings/", { alert_emails_enabled: value })
      .then(() => toast(value ? t("company.emails.enabledToast") : t("company.emails.disabledToast"), ToastTypes.SUCCESS))
      .catch(() => {
        setData((d) => ({ ...d, company: { ...d.company, alert_emails_enabled: !value } }));
        toast(t("company.saveError"), ToastTypes.ERROR);
      })
      .finally(() => setRowBusy("master", false));
  };

  const changeRole = (user, role) => {
    setBusy((b) => ({ ...b, [`r${user.id}`]: true }));
    axiosInstance
      .patch(`company/users/${user.id}/role/`, { role })
      .then((response) => {
        patchList("users", user.id, { role: response.data.role });
        toast(t("roles.changed", { name: user.first_name || user.email, role: t(`roles.names.${role}`) }), ToastTypes.SUCCESS);
      })
      .catch((err) => toast(err.response?.data?.error || t("company.saveError"), ToastTypes.ERROR))
      .finally(() => setBusy((b) => ({ ...b, [`r${user.id}`]: false })));
  };

  const toggleUser = (user, field, value) => {
    patchList("users", user.id, { [field]: value });
    setRowBusy(`u${user.id}`, true);
    axiosInstance
      .patch(`company/users/${user.id}/alerts/`, { [field]: value })
      .catch(() => {
        patchList("users", user.id, { [field]: !value });
        toast(t("company.saveError"), ToastTypes.ERROR);
      })
      .finally(() => setRowBusy(`u${user.id}`, false));
  };

  const toggleRecipient = (rcpt, field, value) => {
    patchList("recipients", rcpt.id, { [field]: value });
    setRowBusy(`r${rcpt.id}`, true);
    axiosInstance
      .patch(`company/recipients/${rcpt.id}/`, { [field]: value })
      .catch(() => {
        patchList("recipients", rcpt.id, { [field]: !value });
        toast(t("company.saveError"), ToastTypes.ERROR);
      })
      .finally(() => setRowBusy(`r${rcpt.id}`, false));
  };

  const removeRecipient = (rcpt) => {
    setRowBusy(`r${rcpt.id}`, true);
    axiosInstance
      .delete(`company/recipients/${rcpt.id}/`)
      .then(() => {
        setData((d) => ({ ...d, recipients: d.recipients.filter((r) => r.id !== rcpt.id) }));
        toast(t("company.emails.removed", { email: rcpt.email }), ToastTypes.SUCCESS);
      })
      .catch(() => toast(t("company.saveError"), ToastTypes.ERROR))
      .finally(() => {
        setRowBusy(`r${rcpt.id}`, false);
        setConfirmDelete(null);
      });
  };

  const addRecipient = (event) => {
    event.preventDefault();
    if (!newRecipient.email.trim()) {
      setAddError(t("company.emails.emailRequired"));
      return;
    }
    setAdding(true);
    setAddError("");
    axiosInstance
      .post("company/recipients/", { ...newRecipient, email: newRecipient.email.trim(), name: newRecipient.name.trim() })
      .then((response) => {
        setData((d) => ({
          ...d,
          recipients: [...d.recipients, response.data].sort((a, b) => a.email.localeCompare(b.email)),
        }));
        setNewRecipient(EMPTY_NEW);
        toast(t("company.emails.added", { email: response.data.email }), ToastTypes.SUCCESS);
      })
      .catch((err) => setAddError(firstError(err?.response?.data, t("company.saveError"))))
      .finally(() => setAdding(false));
  };

  const enabled = company.alert_emails_enabled;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>
            <FontAwesomeIcon icon={faEnvelope} className={styles.cardTitleIcon} />
            {t("company.emails.title")}
          </div>
          <div className={styles.cardHelp}>{t("company.emails.help")}</div>
        </div>
      </div>

      <div className={styles.masterSwitch}>
        <div className={styles.masterSwitchText}>
          {t("company.emails.master")}
          <span>{enabled ? t("company.emails.masterOn") : t("company.emails.masterOff")}</span>
        </div>
        <Toggle checked={enabled} disabled={!!busy.master} label={t("company.emails.master")} onChange={toggleMaster} />
      </div>

      <div className={enabled ? "" : styles.disabledBlock}>
        <div className={styles.categories}>
          {CATEGORIES.map((c) => (
            <div key={c.key} className={styles.category}>
              <strong>{t(`company.emails.cat.${c.key}`)}</strong>
              {t(`company.emails.catHelp.${c.key}`)}
            </div>
          ))}
        </div>

        <div className={styles.sectionLabel}>{t("company.emails.users", { count: users.length })}</div>
        <div className={styles.list}>
          <div className={`${styles.listHeader} ${styles.usersGrid}`}>
            <div>{t("company.emails.user")}</div>
            {CATEGORIES.map((c) => (
              <div key={c.key} className={styles.centerCell}>{t(`company.emails.cat.${c.key}`)}</div>
            ))}
            <div />
          </div>
          {users.length === 0 && <div className={styles.emptyRow}>{t("company.emails.noUsers")}</div>}
          {users.map((u) => (
            <div key={u.id} className={`${styles.listRow} ${styles.usersGrid}`}>
              <div className={styles.personCell} style={{ minWidth: 0 }}>
                <div className={styles.personName}>
                  {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || "—"}
                  {u.is_me && <span className={styles.meBadge}>{t("company.emails.me")}</span>}
                </div>
                <div className={styles.personEmail}>{u.email || t("company.emails.noEmail")}</div>
                <div style={{ marginTop: 4 }}>
                  {u.is_me || data.my_role !== "admin" ? (
                    <span className={styles.roleBadge}>{t(`roles.names.${u.role || "operator"}`)}</span>
                  ) : (
                    <select
                      className={styles.roleSelect}
                      value={u.role || "operator"}
                      disabled={!!busy[`r${u.id}`]}
                      onChange={(e) => changeRole(u, e.target.value)}
                      aria-label={t("roles.change")}
                    >
                      {["viewer", "operator", "admin"].map((r) => (
                        <option key={r} value={r}>{t(`roles.names.${r}`)}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <CategoryToggles
                row={u}
                busy={!!busy[`u${u.id}`] || !u.email}
                t={t}
                onToggle={(field, value) => toggleUser(u, field, value)}
              />
              <div />
            </div>
          ))}
        </div>

        <div className={styles.sectionLabel}>{t("company.emails.extra", { count: recipients.length })}</div>
        <div className={styles.list}>
          <div className={`${styles.listHeader} ${styles.recipientsGrid}`}>
            <div>{t("company.emails.address")}</div>
            {CATEGORIES.map((c) => (
              <div key={c.key} className={styles.centerCell}>{t(`company.emails.cat.${c.key}`)}</div>
            ))}
            <div />
          </div>
          {recipients.length === 0 && <div className={styles.emptyRow}>{t("company.emails.noExtra")}</div>}
          {recipients.map((r) => (
            <div key={r.id} className={`${styles.listRow} ${styles.recipientsGrid}`}>
              <div className={styles.personCell} style={{ minWidth: 0 }}>
                <div className={styles.personName}>{r.name || r.email}</div>
                {r.name && <div className={styles.personEmail}>{r.email}</div>}
              </div>
              <CategoryToggles
                row={r}
                busy={!!busy[`r${r.id}`]}
                t={t}
                onToggle={(field, value) => toggleRecipient(r, field, value)}
              />
              <div className={styles.centerCell}>
                {confirmDelete === r.id ? (
                  <button
                    type="button"
                    className={`${styles.ghostButton} ${styles.dangerButton}`}
                    disabled={!!busy[`r${r.id}`]}
                    onClick={() => removeRecipient(r)}
                    onBlur={() => setConfirmDelete(null)}
                    autoFocus
                  >
                    {t("company.emails.confirmRemove")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.ghostButton}
                    aria-label={t("company.emails.remove", { email: r.email })}
                    title={t("company.emails.remove", { email: r.email })}
                    onClick={() => setConfirmDelete(r.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <form className={styles.addForm} onSubmit={addRecipient} noValidate>
          <input
            type="email"
            className={styles.input}
            placeholder={t("company.emails.emailPlaceholder")}
            value={newRecipient.email}
            onChange={(e) => {
              setNewRecipient((n) => ({ ...n, email: e.target.value }));
              setAddError("");
            }}
            aria-label={t("company.emails.address")}
          />
          <input
            type="text"
            className={styles.input}
            placeholder={t("company.emails.namePlaceholder")}
            value={newRecipient.name}
            maxLength={100}
            onChange={(e) => setNewRecipient((n) => ({ ...n, name: e.target.value }))}
            aria-label={t("company.emails.name")}
          />
          <button type="submit" className={styles.secondaryButton} disabled={adding}>
            <FontAwesomeIcon icon={faPlus} />
            {t("company.emails.add")}
          </button>
          <div className={styles.addChecks}>
            {CATEGORIES.map((c) => (
              <label key={c.key} className={styles.checkLabel}>
                <Toggle
                  checked={newRecipient[c.field]}
                  label={t(`company.emails.cat.${c.key}`)}
                  onChange={(value) => setNewRecipient((n) => ({ ...n, [c.field]: value }))}
                />
                {t(`company.emails.cat.${c.key}`)}
              </label>
            ))}
          </div>
          {addError && <div className={styles.fieldError} style={{ gridColumn: "1 / -1" }}>{addError}</div>}
        </form>
      </div>
    </div>
  );
}
